<script setup lang="ts">
import { ref } from 'vue'
import { uploadBytesResumable, getDownloadURL, ref as storageRef } from 'firebase/storage'
import { doc, updateDoc } from 'firebase/firestore'
import { storage, db } from '@/firebase'
import { useSyncStore } from '@/stores/sync'
import { useToastStore } from '@/stores/toast'
import { writeAuditLog } from '@/services/audit'

const props = defineProps<{
  incidentId: string
  logoUrl: string | null
  canEdit: boolean
}>()

const syncStore = useSyncStore()
const toast = useToastStore()

const uploading = ref(false)
const progress = ref(0)
const imgError = ref(false)
const fileInput = ref<HTMLInputElement | null>(null)

const MAX_SIZE = 10 * 1024 * 1024

function triggerPicker() {
  if (!props.canEdit) return
  if (syncStore.isOnline === false) {
    toast.add('Upload requires network connection', 'warning', 3000)
    return
  }
  fileInput.value?.click()
}

async function onFileChange(event: Event) {
  const input = event.target as HTMLInputElement
  const file = input.files?.[0]
  if (!file) return
  input.value = ''

  if (!['image/jpeg', 'image/png'].includes(file.type)) {
    toast.add('Only JPEG and PNG images are accepted', 'error', 4000)
    return
  }

  if (file.size > MAX_SIZE) {
    toast.add('Maximum file size is 10 MB', 'error', 4000)
    return
  }

  const ext = file.type === 'image/png' ? 'png' : 'jpg'
  const path = `incidents/${props.incidentId}/secondary_logo.${ext}`

  uploading.value = true
  progress.value = 0
  imgError.value = false

  try {
    const fileRef = storageRef(storage, path)
    const uploadTask = uploadBytesResumable(fileRef, file, { contentType: file.type })

    await new Promise<void>((resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          progress.value = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
        },
        reject,
        resolve,
      )
    })

    const logoUrl = await getDownloadURL(uploadTask.snapshot.ref)

    await updateDoc(doc(db, 'incidents', props.incidentId), {
      secondary_agency_logo: logoUrl,
    })

    await writeAuditLog(
      props.incidentId,
      'incident.secondary_logo_uploaded',
      'incident',
      props.incidentId,
      null,
      { secondary_agency_logo: logoUrl },
    )

    // onSnapshot listener updates the store reactively
    toast.add('Secondary logo uploaded', 'success', 2000)
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Upload failed'
    toast.add(msg, 'error', 4000)
  } finally {
    uploading.value = false
    progress.value = 0
  }
}
</script>

<template>
  <div class="flex items-start gap-4">
    <!-- Logo preview + upload -->
    <div
      class="relative group overflow-hidden bg-surface-container-high border border-border shrink-0"
      style="width: 180px; height: 64px;"
      :class="canEdit ? 'cursor-pointer' : 'cursor-default'"
      @click="triggerPicker"
    >
      <img
        v-if="logoUrl && !imgError"
        :src="logoUrl"
        alt="Secondary agency logo"
        class="w-full h-full object-contain p-1"
        @error="imgError = true"
      />
      <div
        v-else
        class="w-full h-full flex items-center justify-center text-outline-variant text-xs"
      >
        No logo set
      </div>

      <!-- Upload progress overlay -->
      <div
        v-if="uploading"
        class="absolute inset-0 bg-black/50 flex flex-col items-center justify-center gap-1"
      >
        <div class="w-4 h-4 border-2 border-white/30 border-t-white animate-spin" />
        <span class="text-white text-xs font-medium">{{ progress }}%</span>
      </div>

      <!-- Hover overlay -->
      <div
        v-else-if="canEdit"
        class="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
      >
        <span class="text-white text-xs font-mono uppercase tracking-widest bg-black/50 px-2 py-1">
          {{ logoUrl && !imgError ? 'Replace' : 'Upload' }}
        </span>
      </div>
    </div>

    <div v-if="logoUrl && !imgError" class="text-xs text-outline self-center">
      Click the logo to replace it.
    </div>
    <div v-else-if="canEdit" class="text-xs text-outline self-center">
      Click to upload a JPEG or PNG (max 10 MB).
    </div>

    <!-- Hidden file input -->
    <input
      ref="fileInput"
      type="file"
      accept="image/jpeg,image/png"
      class="hidden"
      @change="onFileChange"
    />
  </div>
</template>
